import { z } from 'zod';
import { ActionConsequence } from './schemas/plans.js';
import { R1Detail, R1_PREP_FIELD_KEYS, type R1PrepFields } from './schemas/r1.js';

/**
 * Fiches R1 (lots AN et AO, CDC « Fiches R1 ») : logique pure commune au serveur et à l'écran.
 *
 * - `r1KeyOf` : clé d'un R1 = `icalUid` du groupe de copies, sinon `googleEventId` de la copie principale
 *   (`groupCalendarCopies`). Sans la date : un R1 déplacé garde ses infos et sa fiche.
 * - `r1MemberIds` : membres présents sur le R1 = propriétaires des copies (`ownerUserIds`) ∪ invités internes
 *   (adresse du domaine de l'agence) qui correspondent à un membre ACTIF ; jamais un client.
 */

/** Domaine des adresses de l'équipe (même valeur par défaut que `isInternalEmail` côté serveur). */
export const R1_INTERNAL_DOMAIN = 'evolusite.fr';
/** Ancien nom (lot AO). */
export const R1_DEFAULT_INTERNAL_DOMAIN = R1_INTERNAL_DOMAIN;

/** Invités de l'événement (`raw.attendees`, adresses en minuscules), lus prudemment. */
export function r1Attendees(raw: unknown): string[] {
  const list = (raw as { attendees?: unknown } | null | undefined)?.attendees;
  return Array.isArray(list)
    ? list.filter((a): a is string => typeof a === 'string').map((a) => a.trim().toLowerCase())
    : [];
}

export interface R1GroupKeySource {
  main: { icalUid: string | null; googleEventId: string };
}

/** Clé du R1 (`R1Key`) d'un groupe de copies d'agenda. */
export function r1KeyOf(group: R1GroupKeySource): string {
  const ical = group.main.icalUid?.trim();
  return ical ? ical : group.main.googleEventId;
}

export interface R1GroupMembersSource {
  /** Membres dont l'agenda contient une copie du R1 (ordre d'arrivée conservé). */
  ownerUserIds: readonly string[];
  /** Invités du R1 (adresses lues dans l'agenda, `raw.attendees` de la copie principale). */
  attendees?: readonly string[] | null;
  /** À défaut d'`attendees` : la copie principale, dont on lit `raw.attendees` (forme de `groupCalendarCopies`). */
  main?: { raw?: unknown; icalUid?: string | null; googleEventId?: string };
}

export interface R1MemberCandidate {
  id: string;
  email: string;
  /** Absent : membre tenu pour actif (l'appelant a déjà filtré). */
  status?: string;
}

const normalizeEmail = (email: string) => email.trim().toLowerCase();

/**
 * Identifiants des membres présents sur le R1, sans doublon : d'abord les propriétaires des copies, puis les
 * invités internes reconnus. Un membre inactif (ou inconnu) n'est jamais retenu.
 */
export function r1MemberIds(
  group: R1GroupMembersSource,
  members: readonly R1MemberCandidate[],
  internalDomain: string = R1_INTERNAL_DOMAIN,
): string[] {
  const active = members.filter((m) => m.status === undefined || m.status === 'ACTIVE');
  const activeIds = new Set(active.map((m) => m.id));
  const byEmail = new Map(active.map((m) => [normalizeEmail(m.email), m.id] as const));
  const suffix = `@${internalDomain.trim().toLowerCase()}`;
  const out: string[] = [];
  const add = (id: string | undefined) => {
    if (id && activeIds.has(id) && !out.includes(id)) out.push(id);
  };
  for (const id of group.ownerUserIds) add(id);
  for (const attendee of group.attendees ?? r1Attendees(group.main?.raw)) {
    const email = normalizeEmail(attendee);
    if (!email.endsWith(suffix)) continue;
    add(byEmail.get(email));
  }
  return out;
}

/** Limites appliquées à la fiche après la réponse de Claude (la consigne les donne aussi). */
export const R1_BRIEF_LIMITS = {
  summarySentences: 3,
  pains: 5,
  competitors: 3,
  questions: 5,
  alerts: 5,
  doubts: 6,
  socials: 6,
  leaders: 5,
  candidates: 3,
  sources: 40,
} as const;

/**
 * Description d'un événement Google en texte : Google la donne parfois en HTML (`<br>`, liens). Balises retirées,
 * sauts de ligne gardés, entités courantes décodées ; vide = null.
 */
export function eventDescriptionText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return text === '' ? null : text;
}

/** Champs vides : tous à null. */
export function emptyR1PrepFields(): R1PrepFields {
  return Object.fromEntries(R1_PREP_FIELD_KEYS.map((k) => [k, null])) as R1PrepFields;
}

/** Nettoyage d'un champ saisi : espaces retirés, chaîne vide = null. */
export function cleanR1PrepFields(fields: Partial<Record<keyof R1PrepFields, string | null>>): R1PrepFields {
  const out = emptyR1PrepFields();
  for (const key of R1_PREP_FIELD_KEYS) {
    const value = fields[key];
    out[key] = typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
  }
  return out;
}

/** Clé du canal « Je ne sais pas » (`R1PrepFields.channel`). */
export const R1_UNKNOWN_CHANNEL = 'unknown';

/**
 * Infos du R1 enregistrées par un membre (champs remplis ou « Rien à ajouter ») : la carte « Compléter le R1 »
 * n'a plus lieu d'être. Un pré-remplissage seul (depuis la description de l'agenda) ne compte pas.
 */
export function r1PrepAnswered(prep: { nothingToAdd: boolean; filledAt: string | null } | null): boolean {
  return !!prep && (prep.nothingToAdd || prep.filledAt !== null);
}

/** Réponse de `PUT /api/r1/:r1Key/prep` : le volet relu et les cartes changées par les règles qui suivent. */
export const R1PrepResult = z.object({ detail: R1Detail, consequences: z.array(ActionConsequence) });
export type R1PrepResult = z.infer<typeof R1PrepResult>;
